import { useCallback, useEffect, useMemo } from "react";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { getCsrfHeader } from "../lib/csrf";
import { serverErrorMessage } from "../lib/apiError";
import { useCachedQuery } from "./useCachedQuery";

// Mirrors PluginCatalogEntry on the backend: what this build can offer,
// generated from plugins/ by `bin/plugin pack` and shipped in the image.
export type PluginCatalogEntry = {
  id: string;
  name: string;
  description: string;
  version: string;
  openclawVersion: string;
  tools: ReadonlyArray<string>;
  // Part of the fleet default set: every tenant holds it, reconciled by
  // site.yml rather than granted per tenant.
  default: boolean;
};

// Mirrors TenantPluginView: the catalog joined onto one tenant's install
// state. `available` means the catalog offers it and this tenant has no row.
export type TenantPluginState =
  | "available"
  | "pending"
  | "installing"
  | "installed"
  | "failed";

export type TenantPlugin = {
  pluginId: string;
  name: string;
  description: string;
  desiredVersion: string;
  installedVersion: string | null;
  catalogVersion: string | null;
  state: TenantPluginState;
  lastError: string | null;
  requestedBy: string | null;
  updatedAt: string;
  default: boolean;
  // What the instance reports from its install receipt, rather than what was asked for.
  boxVersion: string | null;
  // An entitlement row exists for this tenant, which is the only thing
  // Remove deletes. A default can have one when an admin installed it by hand.
  entitled: boolean;
};

export type AdminPluginsResult<T> = { ok: true; value: T } | { ok: false; message: string };

const CATALOG_URL = ADMIN_QUERIES.plugins.url;
const tenantUrl = (tenantId: string): string =>
  `${CATALOG_URL}/tenants/${encodeURIComponent(tenantId)}`;

// An install takes minutes on the box, so while one is in flight the tenant
// view refreshes far more often than the standard dwell interval. Once
// everything has settled it drops back, because nothing else here changes
// without someone clicking.
const INSTALLING_POLL_MS = 5_000;
const IDLE_POLL_MS = 30_000;

export type UseAdminPluginsResult = {
  catalogStatus: "loading" | "ready" | "error";
  catalog: ReadonlyArray<PluginCatalogEntry>;
  catalogError: string | null;

  tenantStatus: "loading" | "ready" | "error";
  tenantPlugins: ReadonlyArray<TenantPlugin>;
  tenantError: string | null;
  isFetchingTenant: boolean;
  refetchTenant: () => Promise<void>;

  install: (tenantId: string, pluginId: string) => Promise<AdminPluginsResult<void>>;
  remove: (tenantId: string, pluginId: string) => Promise<AdminPluginsResult<void>>;
};

const fetchCatalog = async (signal: AbortSignal): Promise<ReadonlyArray<PluginCatalogEntry>> => {
  const res = await fetch(CATALOG_URL, { credentials: "include", signal });
  if (!res.ok) throw new Error(`Failed to load the plugin catalog (${res.status})`);
  return (await res.json()) as ReadonlyArray<PluginCatalogEntry>;
};

export const useAdminPlugins = (selectedTenantId: string | null): UseAdminPluginsResult => {
  const catalogQuery = useCachedQuery<ReadonlyArray<PluginCatalogEntry>>({
    cacheResource: ADMIN_QUERIES.plugins.resource,
    queryKey: CATALOG_URL,
    fetcher: fetchCatalog,
  });

  const tenantQuery = useCachedQuery<ReadonlyArray<TenantPlugin>>({
    // Network-only: the payload is per-tenant, so caching it under one admin
    // resource would seed the next tenant's view with the previous tenant's
    // rows.
    cacheResource: null,
    queryKey: selectedTenantId === null ? "" : tenantUrl(selectedTenantId),
    fetcher: async (signal) => {
      if (selectedTenantId === null) return [];
      const res = await fetch(tenantUrl(selectedTenantId), { credentials: "include", signal });
      if (!res.ok) throw new Error(`Failed to load this instance's plugins (${res.status})`);
      return (await res.json()) as ReadonlyArray<TenantPlugin>;
    },
    refreshIntervalMs: IDLE_POLL_MS,
  });

  const tenantPlugins = useMemo(() => tenantQuery.data ?? [], [tenantQuery.data]);
  const installing = tenantPlugins.some((plugin) => plugin.state === "installing");
  const { refetch: refetchTenant } = tenantQuery;

  // An install is minutes of work on the box and the row is the only progress
  // signal, so poll faster while one is in flight. This rides on top of the
  // standard dwell interval rather than replacing it, and stops the moment
  // nothing is installing.
  useEffect(() => {
    if (!installing) return;
    const timer = window.setInterval(() => {
      void refetchTenant();
    }, INSTALLING_POLL_MS);
    return () => window.clearInterval(timer);
  }, [installing, refetchTenant]);

  const install = useCallback(
    async (tenantId: string, pluginId: string): Promise<AdminPluginsResult<void>> => {
      try {
        const res = await fetch(`${tenantUrl(tenantId)}/install`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ pluginId }),
        });
        // 202: claimed and queued. The row already reads `installing`, so the
        // refetch below puts the UI in the right state immediately.
        if (res.status === 202) {
          await refetchTenant();
          return { ok: true, value: undefined };
        }
        return { ok: false, message: await serverErrorMessage(res, "Install failed") };
      } catch {
        return { ok: false, message: "Network error" };
      }
    },
    [refetchTenant],
  );

  const remove = useCallback(
    async (tenantId: string, pluginId: string): Promise<AdminPluginsResult<void>> => {
      try {
        const res = await fetch(`${tenantUrl(tenantId)}/${encodeURIComponent(pluginId)}`, {
          method: "DELETE",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        });
        if (res.status === 204) {
          await refetchTenant();
          return { ok: true, value: undefined };
        }
        return { ok: false, message: await serverErrorMessage(res, "Remove failed") };
      } catch {
        return { ok: false, message: "Network error" };
      }
    },
    [refetchTenant],
  );

  return {
    catalogStatus: catalogQuery.status,
    catalog: catalogQuery.data ?? [],
    catalogError: catalogQuery.error,

    tenantStatus: selectedTenantId === null ? "ready" : tenantQuery.status,
    tenantPlugins,
    tenantError: tenantQuery.error,
    isFetchingTenant: tenantQuery.isFetching,
    refetchTenant,

    install,
    remove,
  };
};
